import { useMemo } from 'react';

import type Big from 'big.js';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';

import { useTabsState } from '@cast/design-system';
import type { DateRange } from '@cast/shared-types';
import { WorkloadTrafficDestinationHistoryItem } from '@cast/types';

import { NetworkResourceType } from 'features/cluster/cost-report/network-cost/_types';
import { trafficTickFormatter } from 'features/cluster/cost-report/network-cost/_utils';
import {
  CostReportChart,
  CostReportChartProps,
} from 'features/cost-report/components/chart/CostReportChart';
import { CostReportChartNoResults } from 'features/cost-report/components/chart/CostReportChartNoResults';
import { ChartConfig, ChartType } from 'types/charts';
import { TimeSeries } from 'types/metrics';

import { makeTopDestinationsTooltip } from './TopDestinationsChartTooltip';
import { CostReportChartType } from '../../../../../../cost-report/types/costOverTime';
import { TOP_DESTINATIONS_COLOR_MAP } from '../_constants';
import { Datapoint } from '../_types';
import { fillGaps, mapToDatapoint } from '../_utils';

type Props = {
  data?: WorkloadTrafficDestinationHistoryItem<Big>[];
  range: DateRange;
  timezone: string;
};

export const TopDestinationsChart = ({ data = [], range, timezone }: Props) => {
  const context = useTabsState<NetworkResourceType>();

  const topItems = useMemo(() => data?.slice(0, 5) || [], [data]);
  const topItemsHistory = useMemo<TimeSeries<Datapoint>>(() => {
    if (!data?.length || !context?.currentTab) {
      return fillGaps(range, []);
    }

    const sortedDatapoints = flatMap(
      topItems.map((item, index) =>
        orderBy(item.history || [], ['timestamp'], ['asc']).map((item) =>
          mapToDatapoint(context.currentTab, item, index + 1)
        )
      )
    );

    let result = Object.values(groupBy(sortedDatapoints, 'timestamp')).map(
      (group) => merge({} as Datapoint, ...group)
    );

    result = fillGaps(range, result, 'asc');

    return result;
  }, [context, data?.length, range, topItems]);

  const chartConfig: ChartConfig[] = useMemo(() => {
    if (!data?.length) {
      return [];
    }

    return data?.slice(0, 5)?.map((item, index) => ({
      enabled: true,
      type: ChartType.LINE,
      xAxisId: 0,
      config: {
        dataKey: 'workload' + (index + 1),
        fill: TOP_DESTINATIONS_COLOR_MAP[index],
        strokeWidth: 2,
      },
    }));
  }, [data]);
  const { tooltipHeader, tooltipFooter } = useMemo(() => {
    return makeTopDestinationsTooltip({
      topItems,
      timezone,
      resourceType: context?.currentTab || NetworkResourceType.COSTS,
    });
  }, [context?.currentTab, timezone, topItems]);

  const reportChartProps: CostReportChartProps<Datapoint> = {
    syncId: 'top-destinations-chart',
    chartType: CostReportChartType.LINE,
    tooltipHeader,
    tooltipFooter,
    hideSeriesInTooltip: true,
    height: 215,
    data: topItemsHistory,
    config: chartConfig,
    yAxisProps: {
      ...(context!.currentTab === NetworkResourceType.TRAFFIC && {
        tickFormatter: trafficTickFormatter,
      }),
    },
  };

  if (!data?.length) {
    return <CostReportChartNoResults {...reportChartProps} />;
  }

  return <CostReportChart {...reportChartProps} timezone={timezone} />;
};
